import { useState, useCallback } from 'react';
import { useClearStaleEmrAttemptWhenPushFailedReleased } from './useClearStaleEmrAttemptWhenPushFailedReleased';
import { autoAddInKinnser as autoAddInKinnserEndpoint } from '../../services/api';
import type { AutoAddInKinnserResponse } from '../../services/api';
import {
  getSurfaceableError,
  type SurfaceableResultFields,
} from '../../utils/apiErrorDetail';

export type KinnserAutoAddFlowResult = (
  | AutoAddInKinnserResponse
  | { success: false; message: string }
) &
  SurfaceableResultFields;

interface UseKinnserAutoAddFlowOptions {
  intakeId: string | undefined;
  /** Global intake status; used to clear stale client errors after push_failed is reset. */
  intakeStatus?: string | null;
  getValues: () => {
    kinnser_clinician_id?: string | null;
    /** Current Start of Care date from form (used at schedule click to avoid stale intake-derived value). */
    kinnser_soc_date?: string | null;
    /** Current Episode start date from form (used at schedule click to avoid stale intake-derived value). */
    kinnser_episode_start_date?: string | null;
  };
}

interface UseKinnserAutoAddFlowReturn {
  autoAddInKinnser: boolean;
  autoAddResult: KinnserAutoAddFlowResult | null;
  handleAutoAddInKinnser: () => Promise<void>;
}

/**
 * Hook for SOC "new admission + episode" or "new episode" flow in Kinnser.
 *
 * The backend encapsulates the multi-step flow (auto-add → upload → update details)
 * and records push_failed / Slack notification for 5xx/unexpected failures.
 */
export function useKinnserAutoAddFlow({
  intakeId,
  intakeStatus,
  getValues,
}: UseKinnserAutoAddFlowOptions): UseKinnserAutoAddFlowReturn {
  const [autoAddInKinnser, setAutoAddInKinnser] = useState(false);
  const [autoAddResult, setAutoAddResult] =
    useState<KinnserAutoAddFlowResult | null>(null);

  const clearAutoAddResult = useCallback(() => {
    setAutoAddResult(null);
  }, []);
  useClearStaleEmrAttemptWhenPushFailedReleased(
    intakeId,
    intakeStatus,
    clearAutoAddResult
  );

  const handleAutoAddInKinnser = useCallback(async () => {
    setAutoAddInKinnser(true);
    setAutoAddResult(null);

    try {
      if (!intakeId?.trim()) {
        setAutoAddResult({ success: false, message: 'No intake selected' });
        return;
      }

      const formValues = getValues();
      const clinicianId =
        formValues.kinnser_clinician_id &&
        typeof formValues.kinnser_clinician_id === 'string' &&
        formValues.kinnser_clinician_id.trim() !== ''
          ? formValues.kinnser_clinician_id.trim()
          : null;

      // Use current form values at click time so we send what the user sees (e.g. Start of Care date).
      // Intake-derived props can be stale if the user edited the form before save/refetch.
      const formSocRaw = formValues.kinnser_soc_date;
      const formSoc =
        formSocRaw && typeof formSocRaw === 'string' && formSocRaw.trim() !== ''
          ? formSocRaw.trim().split('T')[0]
          : null;
      const formEpisodeRaw = formValues.kinnser_episode_start_date;
      const formEpisode =
        formEpisodeRaw &&
        typeof formEpisodeRaw === 'string' &&
        formEpisodeRaw.trim() !== ''
          ? formEpisodeRaw.trim().split('T')[0]
          : null;

      const response = await autoAddInKinnserEndpoint({
        path: { intake_id: intakeId },
        body: {
          soc_date: formSoc ?? undefined,
          episode_start_date: formEpisode ?? undefined,
          clinician_id: clinicianId ?? undefined,
        },
      });

      if (response.data) {
        setAutoAddResult(response.data);
      } else {
        const surfaceable = getSurfaceableError(response);
        setAutoAddResult(
          surfaceable
            ? {
                success: false,
                message: surfaceable.message,
                surfaceable: true,
                error_code: surfaceable.errorCode,
              }
            : {
                success: false,
                message: 'Auto-add failed. Please try again.',
              }
        );
      }
    } catch (error) {
      console.error('Failed to auto-add in Kinnser:', error);
      const surfaceable = getSurfaceableError(error);
      setAutoAddResult(
        surfaceable
          ? {
              success: false,
              message: surfaceable.message,
              surfaceable: true,
              error_code: surfaceable.errorCode,
            }
          : {
              success: false,
              message: 'An unexpected error occurred. Please try again.',
            }
      );
    } finally {
      setAutoAddInKinnser(false);
    }
  }, [intakeId, getValues]);

  return {
    autoAddInKinnser,
    autoAddResult,
    handleAutoAddInKinnser,
  };
}
